import { useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, BookOpen, PenLine, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { COVER_IMAGE, IMAGES, NAV_LINKS } from "@/lib/site";
import { scrollToId } from "@/lib/lenis";
import { EASE, GhostButton } from "./shared";

const MaskLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const AvailableNow = () => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 1.5, duration: 0.8, ease: EASE }}
    className="inline-flex items-center gap-4 rounded-xl border border-gold/40 bg-lake-teal/40 px-6 py-4 backdrop-blur-sm"
    data-testid="hero-available-now"
  >
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold/15">
      <BookOpen className="h-5 w-5 text-gold-shimmer" />
    </span>
    <span>
      <span className="block font-serif text-xl text-gold-shimmer">Disponibile ora in libreria</span>
      <span className="mt-0.5 block text-xs text-parchment-ivory/60">
        Edizione cartacea · Hever Edizioni · e presso gli eventi della saga
      </span>
    </span>
  </motion.div>
);

const QuickLinks = () => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 2.2, duration: 0.9, ease: EASE }}
    className="mt-14 border-t border-gold/15 pt-6 lg:col-span-12"
    data-testid="hero-quick-links"
  >
    <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-parchment-ivory/45">
      Vai subito a
    </p>
    <div className="flex flex-wrap gap-2">
      {NAV_LINKS.map((l) => (
        <button
          key={l.id}
          data-testid={`hero-quick-link-${l.id}`}
          onClick={() => scrollToId(l.id)}
          className="rounded-full border border-gold/25 bg-lake-teal/30 px-4 py-1.5 text-xs font-medium text-parchment-ivory/75 backdrop-blur-sm transition-colors duration-300 hover:border-gold-shimmer hover:text-gold-shimmer"
        >
          {l.label}
        </button>
      ))}
      <button
        data-testid="hero-quick-link-contatti"
        onClick={() => scrollToId("contatti")}
        className="rounded-full border border-gold/25 bg-lake-teal/30 px-4 py-1.5 text-xs font-medium text-parchment-ivory/75 backdrop-blur-sm transition-colors duration-300 hover:border-gold-shimmer hover:text-gold-shimmer"
      >
        Scrivici
      </button>
    </div>
  </motion.div>
);

const BookCover = () => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 140, damping: 18 });
  const sry = useSpring(ry, { stiffness: 140, damping: 18 });

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    ry.set(px * 16);
    rx.set(-py * 12);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 60, rotateY: -18 }}
      animate={{ opacity: 1, y: 0, rotateY: 0 }}
      transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
      className="relative mx-auto w-64 sm:w-72 lg:w-80"
      style={{ perspective: 1200 }}
    >
      <motion.div
        ref={ref}
        data-testid="hero-book-cover"
        onMouseMove={onMove}
        onMouseLeave={() => {
          rx.set(0);
          ry.set(0);
        }}
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
        className="relative rounded-md"
      >
        <div className="absolute -left-2 top-1 bottom-1 w-3 -skew-y-1 rounded-l-md bg-lake-teal/90 shadow-inner" style={{ transform: "translateZ(-12px)" }} />
        <img
          src={COVER_IMAGE}
          alt="Copertina ufficiale de Il Segreto del Lago di Patrizia Franzina"
          className="relative block w-full rounded-md border border-gold/40 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7),0_0_40px_rgba(197,155,39,0.15)]"
          draggable={false}
        />
        <div className="pointer-events-none absolute inset-0 rounded-md bg-gradient-to-tr from-transparent via-white/5 to-white/15" />
      </motion.div>
      <div className="mx-auto mt-6 h-4 w-3/4 rounded-[100%] bg-black/50 blur-xl" />
    </motion.div>
  );
};

const HeroNewsletter = () => {
  const [email, setEmail] = useState("");
  const [formato, setFormato] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.post("/newsletter", { email, formato: formato || undefined });
      toast.success(res.data.message);
      setEmail("");
      setFormato(null);
    } catch {
      toast.error("Qualcosa è andato storto. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.9, duration: 0.8, ease: EASE }}
      className="flex max-w-md flex-col gap-3"
      data-testid="hero-newsletter-form"
    >
      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          data-testid="hero-newsletter-email-input"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="La tua email per il primo capitolo"
          className="w-full rounded-full border border-gold/30 bg-lake-teal/40 px-5 py-3 text-sm text-parchment-ivory placeholder:text-parchment-ivory/40 backdrop-blur-sm focus:border-gold-shimmer focus:outline-none"
        />
        <button
          data-testid="hero-newsletter-submit-button"
          type="submit"
          disabled={loading}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-ink-dark transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
        >
          {loading ? "Invio…" : "Iscriviti"}
          <Send className="h-4 w-4" />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="hero-newsletter-formato-group">
        <span className="text-[11px] uppercase tracking-[0.2em] text-parchment-ivory/50">
          E dopo il cartaceo?
        </span>
        {["ebook", "podcast"].map((f) => (
          <button
            key={f}
            type="button"
            data-testid={`hero-newsletter-formato-${f}`}
            onClick={() => setFormato(formato === f ? null : f)}
            className={`rounded-full border px-4 py-1.5 text-xs font-semibold capitalize transition-colors duration-300 ${
              formato === f
                ? "border-gold bg-gold text-ink-dark"
                : "border-gold/30 bg-lake-teal/40 text-parchment-ivory/70 hover:border-gold/60"
            }`}
          >
            {f}
          </button>
        ))}
      </div>
    </motion.form>
  );
};

export default function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 160]);
  const mistY = useTransform(scrollY, [0, 800], [0, -60]);

  return (
    <section id="top" className="relative flex min-h-screen items-center overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMAGES.hero}
          alt="Lago d'Orta con l'Isola di San Giulio nella nebbia"
          className="cinematic h-[120%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-lake-deep/65 via-lake-deep/35 to-lake-deep" />
        <div className="absolute inset-0 bg-gradient-to-r from-lake-deep/60 via-lake-deep/10 to-lake-deep/30" />
      </motion.div>
      <motion.div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-lake-teal/30 to-transparent blur-2xl"
        style={{ y: mistY }}
      />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-24 pt-32 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="text-xs uppercase tracking-[0.35em] text-gold sm:text-sm"
            data-testid="hero-kicker"
          >
            Un romanzo di Patrizia Franzina · Ora in libreria
          </motion.p>

          <h1 className="mt-6 font-display text-4xl leading-[1.08] text-parchment-ivory sm:text-5xl lg:text-6xl" data-testid="hero-title">
            <MaskLine delay={0.4}>Il Segreto</MaskLine>
            <MaskLine delay={0.55} className="text-gold-gradient">
              del Lago
            </MaskLine>
          </h1>

          <MaskLine delay={0.75} className="mt-6 max-w-xl">
            <span className="font-serif text-lg italic text-parchment-ivory/90 sm:text-xl" data-testid="hero-tagline">
              Nel cuore del Lago d'Orta, un amore del Seicento riemerge dalle acque.
            </span>
          </MaskLine>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
            className="mt-4 max-w-xl text-sm leading-relaxed text-parchment-ivory/65 sm:text-base"
          >
            IV secolo, 1600, 1989, 2025. Un segreto nascosto da San Giulio, una maledizione che il
            lago inghiotte e un amore che il tempo ha trasformato in leggenda.
          </motion.p>

          <div className="mt-10">
            <p className="mb-3 text-[11px] uppercase tracking-[0.3em] text-parchment-ivory/50">
              Il romanzo è arrivato
            </p>
            <AvailableNow />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7, duration: 0.8, ease: EASE }}
            className="mt-6 max-w-md rounded-xl border border-gold/25 bg-lake-teal/30 p-5 backdrop-blur-sm"
            data-testid="hero-events"
          >
            <p className="text-[11px] uppercase tracking-[0.3em] text-parchment-ivory/50">
              Prossimi appuntamenti
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gold-shimmer">
                  Incontra l'autrice
                </p>
                <p className="mt-1 text-sm leading-relaxed text-parchment-ivory/85">
                  <strong className="text-parchment-ivory">15 novembre</strong> · Mercatino Natalizio di
                  Ameno — stand Un Filo per Natale, cortile RSA Sant'Antonio
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gold-shimmer">
                  Presentazione
                </p>
                <p className="mt-1 text-sm leading-relaxed text-parchment-ivory/85">
                  <strong className="text-parchment-ivory">21 novembre, ore 16:30</strong> ·
                  Biblioteca di Pettenasco
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gold-shimmer">
                  Incontra l'autrice
                </p>
                <p className="mt-1 text-sm leading-relaxed text-parchment-ivory/85">
                  <strong className="text-parchment-ivory">29 novembre</strong> · Festa di Natale,
                  struttura +Sport — San Maurizio d'Opaglio
                </p>
              </div>
            </div>
            <button
              data-testid="hero-events-link"
              onClick={() => scrollToId("eventi")}
              className="mt-4 text-xs font-semibold text-gold-shimmer underline decoration-gold/40 underline-offset-4 transition-colors hover:decoration-gold-shimmer"
            >
              Tutti i dettagli nella sezione Eventi →
            </button>
          </motion.div>

          <div className="mt-10 flex flex-col gap-6">
            <HeroNewsletter />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.1, duration: 0.8 }}
              className="flex flex-wrap items-center gap-4"
            >
              <GhostButton data-testid="hero-discover-button" onClick={() => scrollToId("libro")}>
                Scopri il romanzo
                <ArrowRight className="h-4 w-4" />
              </GhostButton>
              <a
                data-testid="hero-signed-copy-link"
                href="mailto:autrice@ilsegretodellago.com?subject=Copia%20con%20dedica%20e%20autografo%20—%20Il%20Segreto%20del%20Lago"
                className="inline-flex items-center gap-2.5 rounded-full border border-gold/40 bg-gold/10 px-5 py-3 text-sm font-semibold text-gold-shimmer transition-colors duration-300 hover:border-gold-shimmer hover:bg-gold/20"
              >
                <PenLine className="h-4 w-4" />
                Copia con dedica e autografo: scrivici
              </a>
            </motion.div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <BookCover />
        </div>

        <QuickLinks />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 md:block"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="h-10 w-px bg-gradient-to-b from-gold/70 to-transparent"
        />
      </motion.div>
    </section>
  );
}
